'use client';

import { type FC } from 'react';

import { type GlycerinButtonClickPayload } from '@packages/tracking/src/types/payloads';
import { type ButtonProps } from '../Button/Button';
import { CircularProgress } from '../CircularProgress/CircularProgress';
import {
  ColorButtonTooltip,
  ColorButtonTooltipProvider,
  ColorButtonTooltipTrigger,
  ColorButtonTooltipContent,
  ColorButtonTooltipArrow,
  ColorButtonTooltipPortal,
} from './ColorButtonTooltip/ColorButtonTooltip';

export type ColorButtonProps = {
  /** color name */
  name: string;
  /** color value for css background color */
  color?: string;
  /** color (pattern) image url */
  image?: string;
  /** should the image be fitted into the button
   * @default false
   */
  fitBackgroundImage?: boolean;
  /** if color is active
   * @default false
   */
  active?: boolean;
  /** small color button
   * @default true
   */
  minimal?: boolean;
  /** rounded color button
   * @default true
   */
  rounded?: boolean;
  /** if the button is selected
   * @default false
   */
  isSelected?: boolean;
  clickTrackingProps?: GlycerinButtonClickPayload;
} & Omit<ButtonProps, 'color' | 'children'>;

/**
 * ColorButton component based on https://mui.com/material-ui/react-button/
 * */
export const ColorButton: FC<ColorButtonProps> = ({
  name,
  color,
  image,
  fitBackgroundImage = false,
  // TODO remove, in favor of `selected` or `isSelected` and an explicit `loading` or `isLoading` prop
  active = false,
  // TODO change to string union, mirroring what is passed into the `Button`
  minimal = true,
  // TODO change to default false, as best practice for boolean props
  rounded = true,
  isSelected = false,
  disabled,
  onClick,
  className,
  ...rest
}) => {
  const isLoading = isSelected && !active;

  return (
    <ColorButtonTooltipProvider delayDuration={0}>
      <ColorButtonTooltip>
        <ColorButtonTooltipTrigger asChild>
          {/*  using html native button to simplify the styles which saves a lot of effort (due to asChild prop, shared UI Button needs more styling to match Mui) */}
          <button
            aria-busy={isLoading ? 'true' : 'false'}
            aria-label={name}
            type="button"
            className={`border-color-grey relative ${minimal ? 'min-h-[30px] min-w-[30px]' : 'min-h-[36px] min-w-[36px]'} border-2 ${rounded ? 'rounded-full' : ''} ${active ? 'outline-2' : ''} grid place-items-center ${className}`}
            style={{
              background: `${color || 'transparent'} url(${image}) center repeat`,
              ...(fitBackgroundImage && { backgroundSize: 'cover' }),
            }}
            onClick={(e) => {
              if (isLoading || isSelected) return;
              e.stopPropagation();
              onClick?.(e);
            }}
            disabled={disabled}
            {...rest}
          >
            {isLoading && (
              <>
                <span
                  className={`bg-color-background-base ${rounded ? 'rounded-full' : ''} pointer-events-none absolute inset-0 z-10 opacity-80`}
                />
                <CircularProgress
                  className={`pointer-events-none absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 ${minimal ? 'min-h-[30px] min-w-[30px]' : 'min-h-[36px] min-w-[36px]'}`}
                />
              </>
            )}
          </button>
        </ColorButtonTooltipTrigger>
        <ColorButtonTooltipPortal>
          <ColorButtonTooltipContent side="bottom">
            {name}
            <ColorButtonTooltipArrow className="fill-color-background-base h-1 drop-shadow-md" />
          </ColorButtonTooltipContent>
        </ColorButtonTooltipPortal>
      </ColorButtonTooltip>
    </ColorButtonTooltipProvider>
  );
};
